import React, { useState } from 'react'
import ReactModal from 'react-modal'
import { useTranslation } from 'react-i18next'
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg'
import { ReactComponent as Calendar } from '../../assets/svg/Calendar.svg'
import { ReactComponent as DeleteIcon } from '../../assets/svg/DeleteIcon.svg'
import { ReactComponent as Save } from '../../assets/svg/Save.svg'
import { ReactComponent as LockIcon } from '../../assets/svg/LockIcon.svg'
import { deleteScheduledJob } from '../../server/server'
import { notify } from '../../utils/util'

function ScheduledEmailsModal({ isOpen, onClose, scheduledEmails, onAfterOpen, onEmailDeleted }) {
    const { t } = useTranslation()
    const [isDeleting, setIsDeleting] = useState(false)

    const handleDeleteEmail = async (id, emailLabel) => {
        if (!window.confirm(t('Are you sure you want to delete this scheduled email?'))) {
            return
        }

        setIsDeleting(true)
        try {
            const response = await deleteScheduledJob(id)
            if (response.status === 200) {
                notify(t('Scheduled email deleted successfully'), 'success')
                if (onEmailDeleted) {
                    onEmailDeleted(id)
                }
            } else {
                notify(t('Failed to delete scheduled email'), 'error')
            }
        } catch (error) {
            console.error('Error deleting scheduled email:', error)
            notify(t('Error deleting scheduled email'), 'error')
        } finally {
            setIsDeleting(false)
        }
    }

    const formatDate = (dateString) => {
        try {
            // Handle MongoDB date format
            if (typeof dateString === 'object' && dateString.$date) {
                return new Date(dateString.$date).toLocaleString()
            }
            return new Date(dateString).toLocaleString()
        } catch {
            return dateString || 'N/A'
        }
    }

    return (
        <ReactModal
            isOpen={isOpen}
            onRequestClose={onClose}
            contentLabel="Scheduled Emails Modal"
            ariaHideApp={false}
            onAfterOpen={onAfterOpen}
            style={{
                overlay: {
                    zIndex: 100,
                },
                content: {
                    inset: 0,
                    padding: 0,
                    margin: 'auto',
                    marginTop: 40,
                    opacity: 1,
                    width: 700,
                    backgroundColor: 'transparent',
                    maxHeight: '92vh',
                    border: 'none',
                    borderRadius: 10,
                    boxShadow: 'none',
                    overflow: 'hidden',
                    position: 'relative',
                },
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div className="day-reflection-modal-icon" onClick={onClose}>
                    <CloseModalIcon />
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-lg mt-[30px]">
                {/* Header */}
                <div className="pt-[49px] pb-[22px] rounded-[10px] bg-lightBG relative">
                    <h2 className="text-xl font-bold text-deepPurple text-center">{t('Scheduled Emails')}</h2>
                </div>

                {/* Content */}
                <div className="max-h-[calc(92vh-126px)] overflow-auto grey-small-scroll p-6">
                    {scheduledEmails.length === 0 ? (
                        <div className="text-center py-8">
                            <Calendar className="w-16 h-16 text-mudPurple mx-auto mb-4" />
                            <h3 className="text-mudPurple text-lg font-medium">{t('No scheduled emails found')}</h3>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {scheduledEmails.map((email) => (
                                <div key={email.id} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm transition-shadow duration-200">
                                    <div className="flex flex-col space-y-4">
                                        {/* Header with title, status, and delete button */}
                                        <div className="flex items-start justify-between">
                                            <h3 className="text-xl font-semibold text-deepPurple leading-tight">
                                                {email.label || `${email.userSelection === 'all' ? 'All' : 'New'} ${email.targetGroup} Email`}
                                            </h3>
                                            <div className="flex items-center gap-3">
                                                <span
                                                    className={`px-4 py-2 rounded-full text-sm font-semibold shadow-sm ${
                                                        email.done ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                                                    }`}
                                                >
                                                    {email.done ? t('Sent') : t('Pending')}
                                                </span>
                                                {!email.done && (
                                                    <button
                                                        onClick={() => handleDeleteEmail(email.id, email.label)}
                                                        disabled={isDeleting}
                                                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors duration-200 disabled:opacity-50"
                                                        title={t('Delete scheduled email')}
                                                    >
                                                        <DeleteIcon className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Content grid */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-1">
                                                <span className="text-mudPurple text-sm font-medium block">{t('Target Group')}</span>
                                                <p className="font-semibold text-deepPurple text-lg">
                                                    {email.userSelection === 'all' ? t('All') : t('New')} {email.targetGroup}
                                                </p>
                                            </div>
                                            <div className="space-y-1">
                                                <span className="text-mudPurple text-sm font-medium block">{t('Scheduled Date')}</span>
                                                <p className="font-semibold text-deepPurple text-lg">{formatDate(new Date(email.clientTime))}</p>
                                            </div>
                                        </div>

                                        {/* Additional Options Display */}
                                        {(email.saveToMasterlist || email.disablePasswordPrompt) && (
                                            <div className="border-t border-gray-200 pt-4">
                                                <div className="flex flex-wrap gap-4 items-center">
                                                    <p className="text-sm text-mudPurple">Actions: </p>
                                                    {email.saveToMasterlist && (
                                                        <div className="flex items-center gap-2 text-sm text-mudPurple">
                                                            <Save className="w-4 h-4 text-mudPurple" />
                                                            <span>{t('Save to master list')}</span>
                                                        </div>
                                                    )}
                                                    {email.disablePasswordPrompt && (
                                                        <div className="flex items-center gap-2 text-sm text-mudPurple">
                                                            <LockIcon className="w-4 h-4 text-mudPurple" />
                                                            <span>{t('Disable password prompt')}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </ReactModal>
    )
}

export default ScheduledEmailsModal
